import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  DialogContent,
  FormLabel,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { AxiosError } from "axios";
import {
  AccountStatus,
  AdminDetailUserInfoResponse,
  UserTypeResponse,
} from "@veris-health/user-ms/lib/v1";
import { IconName, Option, VrsButton, VrsDialog, VrsIcon, VrsSelect } from "@veris-health/web-core";
import { VrsFormInputField } from "../../../ui/components/VrsFormInputField";
import {
  createIntegrationClient,
  CreateIntegrationClientResponse,
  IntegrationClientDefaultThresholds,
} from "../api/actionsApi";
import { getUsers } from "../../Users/api/usersApi";
import SnackbarUtils from "../../../utils/SnackbarUtils";
import { ApiErrorResponse } from "../../../constants";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { selectHospitals } from "../../Users/userSlice";

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .min(1, "Name must be at least 1 character")
    .max(255, "Name must be at most 255 characters")
    .matches(/^[a-zA-Z0-9_-]+$/, "Only alphanumeric characters, underscores, and dashes allowed"),
  hospital: yup.number().required("Hospital is required."),
  outboundEndpoint: yup
    .string()
    .url("Must be a valid URL")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  outboundSecretReference: yup
    .string()
    .max(255, "Must be at most 255 characters")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
});

const initialValues = {
  name: "",
  hospital: undefined as number | undefined,
  mainOncologist: undefined as number | undefined,
  outboundEndpoint: "",
  outboundSecretReference: "",
  defaultMinTemp: "",
  defaultMaxTemp: "",
  defaultMinSys: "",
  defaultMaxSys: "",
  defaultMinDia: "",
  defaultMaxDia: "",
  defaultMinHr: "",
  defaultMaxHr: "",
  defaultMinRpm: "",
  defaultMaxRpm: "",
  defaultMinSo2: "",
  defaultMinSteps: "",
};

const buildThresholdsPayload = (
  values: typeof initialValues,
): IntegrationClientDefaultThresholds | undefined => {
  const thresholds: IntegrationClientDefaultThresholds = {};
  const mappings: Array<{ formKey: keyof typeof initialValues; apiKey: keyof IntegrationClientDefaultThresholds }> = [
    { formKey: "defaultMinTemp", apiKey: "default_min_temp" },
    { formKey: "defaultMaxTemp", apiKey: "default_max_temp" },
    { formKey: "defaultMinSys", apiKey: "default_min_sys" },
    { formKey: "defaultMaxSys", apiKey: "default_max_sys" },
    { formKey: "defaultMinDia", apiKey: "default_min_dia" },
    { formKey: "defaultMaxDia", apiKey: "default_max_dia" },
    { formKey: "defaultMinHr", apiKey: "default_min_hr" },
    { formKey: "defaultMaxHr", apiKey: "default_max_hr" },
    { formKey: "defaultMinRpm", apiKey: "default_min_rpm" },
    { formKey: "defaultMaxRpm", apiKey: "default_max_rpm" },
    { formKey: "defaultMinSo2", apiKey: "default_min_so2" },
    { formKey: "defaultMinSteps", apiKey: "default_min_steps" },
  ];
  let hasAny = false;
  mappings.forEach(({ formKey, apiKey }) => {
    const val = values[formKey];
    if (val !== "" && val !== undefined) {
      (thresholds as Record<string, number>)[apiKey] = Number(val);
      hasAny = true;
    }
  });
  return hasAny ? thresholds : undefined;
};

export const AddIntegrationClient = (): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [createdClient, setCreatedClient] = useState<CreateIntegrationClientResponse | null>(null);
  const [doctors, setDoctors] = useState<Option[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const theme = useTheme();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      createIntegrationClient({
        name: values.name,
        // eslint-disable-next-line camelcase
        hospital_id: values.hospital as number,
        provider: "rhapsody",
        // eslint-disable-next-line camelcase
        main_oncologist_id: values.mainOncologist || undefined,
        // eslint-disable-next-line camelcase
        outbound_endpoint: values.outboundEndpoint || undefined,
        // eslint-disable-next-line camelcase
        outbound_secret_reference: values.outboundSecretReference || undefined,
        // eslint-disable-next-line camelcase
        default_thresholds: buildThresholdsPayload(values),
      })
        .then((response: CreateIntegrationClientResponse) => {
          setCreatedClient(response);
          SnackbarUtils.success("Integration client created successfully.");
        })
        .catch((error: AxiosError<ApiErrorResponse>) => {
          const errorMessage =
            error.response?.data?.message ||
            (error.response?.data as { detail?: Array<{ msg: string }> })?.detail?.[0]?.msg ||
            "Something went wrong, please try again";
          SnackbarUtils.error(errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  const hospitals = useAppSelector(selectHospitals);

  const hospitalOptions: Option[] = useMemo(() => {
    return hospitals.map((item) => ({ label: item.name, value: item.id.toString() }));
  }, [hospitals]);

  const selectedHospital = formik.values.hospital;

  useEffect(() => {
    if (!selectedHospital) {
      setDoctors([]);
      return;
    }

    setDoctorsLoading(true);
    setDoctors([]);
    formik.setFieldValue("mainOncologist", undefined);

    getUsers({
      type: [UserTypeResponse.MedStaff],
      hospital: selectedHospital,
      accountStatus: AccountStatus.Active,
    })
      .then((response) => {
        const doctorOptions = response.items
          .filter((user: AdminDetailUserInfoResponse) =>
            user.user_roles?.some((role) => role.role_name === "doctor"),
          )
          .map((user: AdminDetailUserInfoResponse) => ({
            label: user.name ?? `Doctor ${user.id}`,
            value: user.id.toString(),
          }));
        setDoctors(doctorOptions);
      })
      .catch(() => {
        SnackbarUtils.error("Failed to fetch doctors for this hospital.");
      })
      .finally(() => {
        setDoctorsLoading(false);
      });
  }, [selectedHospital]);

  const {
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    touched,
    resetForm,
    values,
    setFieldValue,
    setFieldTouched,
  } = formik;

  const handleClose = useCallback(() => {
    resetForm();
    setCreatedClient(null);
    setDoctors([]);
    setShowModal(false);
  }, [resetForm]);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleCopyApiKey = useCallback(() => {
    // eslint-disable-next-line camelcase
    const apiKey = createdClient?.api_key;
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      SnackbarUtils.success("API key copied to clipboard.");
    }
  }, [createdClient]);

  return (
    <Box pb={2}>
      <VrsButton buttonType="primary" onClick={handleOpenModal}>
        <Typography variant="bodySemibold" fontWeight={400} pt={0.25} pl={2}>
          Add Integration Client
        </Typography>
        <Box pl={1} pt={0.75} pr={2}>
          <VrsIcon name={IconName.Plus} size={18} stroke={theme.veris.colors.neutrals.white} />
        </Box>
      </VrsButton>

      <VrsDialog
        title={createdClient ? "Integration Client Created" : "Add Integration Client"}
        sx={{ "& .MuiPaper-root": { height: "auto" } }}
        open={showModal}
        onClose={handleClose}
        width="600px"
      >
        <DialogContent sx={{ overflowY: "unset" }}>
          {createdClient ? (
            // Success view - show API key
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                Integration client &quot;{createdClient.name}&quot; created successfully.
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Client ID:</strong> {createdClient.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Provider:</strong> {createdClient.provider}
              </Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                This API key will only be shown once. Please copy and store it securely in your
                vault so you can reprocess failed messages.
              </Alert>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: theme.veris.colors.neutrals["grey-1"],
                  borderRadius: 1,
                  p: 2,
                  mb: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                  }}
                >
                  {/* eslint-disable-next-line camelcase */}
                  {createdClient.api_key}
                </Typography>
                <VrsButton buttonType="secondary" onClick={handleCopyApiKey}>
                  Copy
                </VrsButton>
              </Box>

              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <VrsButton buttonType="primary" onClick={handleClose}>
                  Done
                </VrsButton>
              </Grid>
            </Box>
          ) : (
            // Form view
            <FormikProvider value={formik}>
              <form onSubmit={handleSubmit}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  This is an initial version of integration client management. Additional controls
                  may be added as new integration requirements emerge.
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <VrsFormInputField
                      name="name"
                      type="text"
                      label="Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.name && errors.name ? errors.name : " "}
                      error={!!touched.name && Boolean(errors.name)}
                      isValidField={!errors.name && !!values.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2">
                        Hospital
                        <FormLabel required />
                      </Typography>
                      <VrsSelect
                        innerSx={{ padding: theme.spacing(0.75, 0.75, 0.75, 0) }}
                        value={values.hospital?.toString()}
                        options={hospitalOptions}
                        placeholder="Choose hospital"
                        onSelected={(value) => setFieldValue("hospital", value)}
                        onBlur={() => setFieldTouched("hospital", true)}
                        maxHeight="250px"
                      />
                      {touched.hospital && errors.hospital && (
                        <Typography
                          color={theme.veris.colors.errors.normal}
                          variant="caption"
                          component="div"
                          my={1.5}
                        >
                          {errors.hospital}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2">
                        Main Oncologist (optional)
                      </Typography>
                      <VrsSelect
                        innerSx={{ padding: theme.spacing(0.75, 0.75, 0.75, 0) }}
                        value={values.mainOncologist?.toString()}
                        options={doctors}
                        placeholder={
                          // eslint-disable-next-line no-nested-ternary
                          !values.hospital
                            ? "Select hospital first"
                            : doctorsLoading
                              ? "Loading doctors..."
                              : "Choose main oncologist"
                        }
                        disabled={!values.hospital || doctorsLoading}
                        onSelected={(value) => setFieldValue("mainOncologist", Number(value))}
                        maxHeight="250px"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Assigned as main oncologist for patients created via EHR orders
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Provider
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rhapsody
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <VrsFormInputField
                      name="outboundEndpoint"
                      type="text"
                      label="Outbound Endpoint (optional)"
                      value={values.outboundEndpoint}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        touched.outboundEndpoint && errors.outboundEndpoint
                          ? errors.outboundEndpoint
                          : "URL where Veris will send data to the integration partner"
                      }
                      error={!!touched.outboundEndpoint && Boolean(errors.outboundEndpoint)}
                      isValidField={!errors.outboundEndpoint && !!values.outboundEndpoint}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <VrsFormInputField
                      name="outboundSecretReference"
                      type="text"
                      label="Outbound Secret Reference (optional)"
                      value={values.outboundSecretReference}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        touched.outboundSecretReference && errors.outboundSecretReference
                          ? errors.outboundSecretReference
                          : "Key Vault reference for outbound authentication"
                      }
                      error={
                        !!touched.outboundSecretReference &&
                        Boolean(errors.outboundSecretReference)
                      }
                      isValidField={
                        !errors.outboundSecretReference && !!values.outboundSecretReference
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Accordion sx={{ boxShadow: "none", border: `1px solid ${theme.veris.colors.neutrals["grey-2"]}` }}>
                      <AccordionSummary>
                        <Typography variant="subtitle2">Default Thresholds (optional)</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                          Set default threshold values for patients onboarded via this integration.
                          Leave blank to use system defaults.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinSys" type="number" label="Systolic BP Min" value={values.defaultMinSys} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMaxSys" type="number" label="Systolic BP Max" value={values.defaultMaxSys} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinDia" type="number" label="Diastolic BP Min" value={values.defaultMinDia} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMaxDia" type="number" label="Diastolic BP Max" value={values.defaultMaxDia} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinHr" type="number" label="Heart Rate Min" value={values.defaultMinHr} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMaxHr" type="number" label="Heart Rate Max" value={values.defaultMaxHr} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinRpm" type="number" label="Resp. Rate Min" value={values.defaultMinRpm} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMaxRpm" type="number" label="Resp. Rate Max" value={values.defaultMaxRpm} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinTemp" type="number" label="Temperature Min (°F)" value={values.defaultMinTemp} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMaxTemp" type="number" label="Temperature Max (°F)" value={values.defaultMaxTemp} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinSo2" type="number" label="SpO2 Min (%)" value={values.defaultMinSo2} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                          <Grid item xs={6}>
                            <VrsFormInputField name="defaultMinSteps" type="number" label="Steps Min" value={values.defaultMinSteps} onChange={handleChange} onBlur={handleBlur} helperText=" " error={false} />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  <Grid item xs={12} marginTop={2} display="flex" justifyContent="flex-end">
                    <VrsButton buttonType="primary" type="submit" disabled={!isValid || !dirty}>
                      Create
                      {isLoading && (
                        <CircularProgress
                          sx={{
                            marginLeft: theme.spacing(2),
                            color: theme.veris.colors.neutrals.white,
                          }}
                          size={16}
                        />
                      )}
                    </VrsButton>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
          )}
        </DialogContent>
      </VrsDialog>
    </Box>
  );
};
